import { useEffect, useMemo } from 'react';
import { styled } from 'styled-components';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { SkeletonBlurb } from 'components/Containers';
import Referral from 'components/patient/blurbs/Referral';
import SafeReturn from 'components/patient/blurbs/SafeReturn';
import AccessGuard from 'components/shared/AccessGuard';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { LeftColumn, RightColumn } from 'components/shared/ResponsivePageLayout';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useAuthorization } from 'context/review/authorization';
import { useEpisodeId } from 'hooks/useEpisodeId';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useKeyDetails from 'hooks/useKeyDetails';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import ProgressUpdate from 'models/activities/ProgressUpdate';
import { getChartData } from 'models/Chart';
import { EpisodeAccessLevel } from 'models/Episode';
import LocationEpisodeSummary from 'models/LocationEpisodeSummary';
import { AssignedUserRole } from 'models/LocationEpisodeUser';
import { PERMISSIONS } from 'models/Profile';
import { activityQueryKeys, indexActivities } from 'services/api/activity';
import { LOCATION_EPISODE_CHANNEL } from 'services/api/constants';
import { progressChartQueryKeys, showProgressChart } from 'services/api/locationEpisode/progressChart';

import ActivityList from './blurbs/activities/ActivityList';
import { AssignedUser } from './blurbs/AssignedUser';
import { Authorization } from './blurbs/Authorization';
import KeyDetails from './blurbs/KeyDetails';
import NewActivity from './blurbs/NewActivity';
import PatientOverview from './blurbs/PatientOverview';
import PatientOverviewSkeleton from './blurbs/patientOverview/PatientOverviewSkeleton';
import PatientStatus from './blurbs/PatientStatus';
import { SkeletonPatientStatus } from './blurbs/PatientStatus';
import { ProjectedDischarge } from './blurbs/ProjectedDischarge';
import ServiceRefusalView from './blurbs/ServiceRefusal';
import { OlioLineChart } from './charts/PatientProgressChart';
import { PatientProgressChart } from './charts/PatientProgressChart';

function StaySelectorOptionLabel(locationEpisode: LocationEpisodeSummary) {
  const { group } = locationEpisode;

  if (!locationEpisode.id) return null;
  return (
    <>
      <span style={{ fontWeight: 'bold' }}>{locationEpisode.stayDescriptor} </span>- {group.name} (
      {group.groupType?.displayName})
    </>
  );
}

const PatientStoryPage = () => {
  const episodeId = useEpisodeId();

  const { episode, loading: episodeLoading } = useEpisode();
  const {
    locationEpisode,
    selectedLocationEpisodeId,
    setSelectedLocationEpisodeId,
    loading: locationEpisodeFetching,
  } = useSelectedLocationEpisode();
  const { profile } = useProfile();
  const { setAuthorizationId } = useAuthorization();
  const invalidateQueries = useInvalidateQueries();
  const {
    entries: keyDetailsEntries,
    isPending: keyDetailsPending,
    postKeyDetails,
    query: keyDetailsQuery,
  } = useKeyDetails({
    locationEpisodeId: selectedLocationEpisodeId,
  });

  useWebsocketSubscription<{
    activities?: boolean;
    locationEpisodeRehabStates?: boolean;
    actorId: string;
  }>({
    channel: LOCATION_EPISODE_CHANNEL,
    params: { location_episode_id: locationEpisode?.id },
    onReceived: (data) => {
      if (data.actorId === profile.id) return;

      if (data.activities) {
        invalidateQueries({
          activities: true,
          ...(locationEpisode && { attachmentsCount: { locationEpisodeId: locationEpisode.id } }),
        });
      }

      if (data.locationEpisodeRehabStates && locationEpisode) {
        invalidateQueries({
          locationEpisode: { id: locationEpisode.id },
        });
      }
    },
    enabled: !!locationEpisode,
  });

  useEffect(() => {
    if (locationEpisode?.umAISummaryEnabled && locationEpisode?.activeAuthorizationReview?.id) {
      setAuthorizationId(locationEpisode.activeAuthorizationReview.id);
    }
  }, [locationEpisode?.umAISummaryEnabled, locationEpisode?.activeAuthorizationReview?.id, setAuthorizationId]);

  const currentGroupTypeClientConfig = locationEpisode?.owner.client.configForManagedGroupType(
    locationEpisode.group.groupTypeId
  );
  const showUtilizationManager =
    currentGroupTypeClientConfig?.utilizationManager &&
    episode &&
    (profile.isManagerFor(episode) || !!locationEpisode?.utilizationManagerUser);

  const showCaseManager =
    currentGroupTypeClientConfig?.caseManager &&
    episode &&
    (profile.isManagerFor(episode) || !!locationEpisode?.caseManagerUser);

  const previousSNFStay = useMemo(() => {
    return episode?.orderedActiveLocationEpisodes?.find(
      (le) => le.group.groupType?.apiName === SKILLED_NURSING_FACILITY && !le.latest
    );
  }, [episode]);

  const showSafeReturn =
    !!currentGroupTypeClientConfig?.safeReturn &&
    !!locationEpisode?.latest &&
    !locationEpisode?.discharged &&
    !!previousSNFStay;

  const isStayActive =
    !!episode && !!locationEpisode && locationEpisode.active && !locationEpisode.archived && !episode.archived;

  const showMainActivityInput = isStayActive && episode?.currentUserAccessLevel === EpisodeAccessLevel.FULL;

  const showReferral = !!currentGroupTypeClientConfig?.referral && locationEpisode?.inTreatment;

  const { data: progressChartData } = useQuery({
    queryKey: progressChartQueryKeys.show(selectedLocationEpisodeId ?? ''),
    queryFn: () => showProgressChart(selectedLocationEpisodeId ?? ''),
    enabled: !!selectedLocationEpisodeId,
  });

  const progressUpdatesQueryParams = {
    locationEpisodeId: selectedLocationEpisodeId,
    include: 'attachments.blob,attachments.fileDeletedByClient',
    type: 'progress_update',
    viewOnly: true,
  };

  const progressUpdatesQuery = useInfiniteQuery({
    queryKey: activityQueryKeys.index(progressUpdatesQueryParams),
    queryFn: ({ pageParam, signal }) => indexActivities({ ...progressUpdatesQueryParams, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: !!selectedLocationEpisodeId,
  });

  const chartData = useMemo(
    () =>
      getChartData(
        progressUpdatesQuery.data?.pages[0]?.data?.filter((activity) => activity instanceof ProgressUpdate) ?? [],
        locationEpisode?.progressTemplate?.questions
      ),
    [progressUpdatesQuery?.data, locationEpisode]
  );

  return (
    <ResponsiveGrid isNavBarOpen={!!episodeId}>
      <LeftColumn>
        {episode ? (
          episode.locationEpisodes?.length > 1 && (
            <Select<LocationEpisodeSummary>
              options={episode?.locationEpisodes ?? []}
              onChange={(change) => setSelectedLocationEpisodeId(change!.id)}
              getOptionValue={({ id }) => id}
              formatOptionLabel={StaySelectorOptionLabel}
              value={episode.locationEpisodes?.find((le) => le.id === selectedLocationEpisodeId)}
            />
          )
        ) : (
          <SkeletonLocationEpisodeDropdown>
            <SkeletonLocationEpisodeDropdownInner />
          </SkeletonLocationEpisodeDropdown>
        )}
        <Authorization
          locationEpisode={locationEpisode}
          episode={episode}
          patientName={episode?.patient.name}
          profile={profile}
        />
        {episode && locationEpisode && profile ? (
          <>
            <ProjectedDischarge
              locationEpisode={locationEpisode}
              profile={profile}
              episode={episode}
              patientName={episode.patient.name!}
            />

            {showUtilizationManager && (
              <AssignedUser
                locationEpisode={locationEpisode}
                episode={episode}
                patientName={episode?.patient.name}
                assignedUser={locationEpisode?.utilizationManagerUser}
                assignedUserRole={AssignedUserRole.UtilizationManager}
              />
            )}
            {showCaseManager && (
              <AssignedUser
                locationEpisode={locationEpisode}
                episode={episode}
                patientName={episode.patient.name!}
                assignedUser={locationEpisode?.caseManagerUser}
                assignedUserRole={AssignedUserRole.CaseManager}
              />
            )}
            {showSafeReturn && (
              <SafeReturn
                profile={profile}
                episode={episode}
                locationEpisode={locationEpisode}
                previousSNFStay={previousSNFStay}
                review={locationEpisode.safeReturnReview}
              />
            )}
            {showReferral && (
              <Referral
                episode={episode}
                locationEpisode={locationEpisode}
                review={locationEpisode.latestReferralReview}
                loading={episodeLoading || locationEpisodeFetching}
              />
            )}
            <PatientOverview episode={episode} locationEpisode={locationEpisode} />
          </>
        ) : (
          <PatientOverviewSkeleton />
        )}
        {progressChartData && <PatientProgressChart chartData={progressChartData} />}
        {chartData.map((chart, index) => chart && <OlioLineChart key={index} chart={chart} />)}
      </LeftColumn>

      <StyledRightColumn>
        {locationEpisode && <ServiceRefusalView locationEpisode={locationEpisode} episode={episode!} />}
        {isStayActive && (
          <>
            <KeyDetails
              editable={profile?.isManagerFor(episode)}
              entries={keyDetailsEntries}
              onPost={postKeyDetails}
              query={keyDetailsQuery}
              submitting={keyDetailsPending}
            />
            <KeyDetailsSeparator />
          </>
        )}
        {!(episode && locationEpisode && profile && !locationEpisodeFetching) ? (
          <SkeletonPatientStatus />
        ) : (
          !episode?.archived && (
            <PatientStatus
              episode={episode}
              locationEpisode={locationEpisode}
              patientName={episode?.patient.name}
              profile={profile}
            />
          )
        )}
        {showMainActivityInput && (
          <AccessGuard accessor={PERMISSIONS.activityNoteCreate} locationEpisode={locationEpisode} episode={episode}>
            <NewActivity
              key={locationEpisode?.id}
              episode={episode}
              locationEpisode={locationEpisode}
              additionalInvalidations={{
                locationEpisode: { id: locationEpisode?.id },
                authorization: { id: locationEpisode?.activeAuthorizationReview?.id },
              }}
            />
          </AccessGuard>
        )}

        <ActivityList episode={episode} profile={profile} locationEpisode={locationEpisode} />
      </StyledRightColumn>
    </ResponsiveGrid>
  );
};

export default PatientStoryPage;

export const ResponsiveGrid = styled.div<{ isNavBarOpen?: boolean }>`
  @media (max-width: ${(props) => (props.isNavBarOpen ? '1050px' : '800px')}) {
    flex-direction: column;
    gap: 36px;
  }
  display: flex;
  flex: 1;
  gap: 0;
  min-height: 0;
`;

const StyledRightColumn = styled(RightColumn)`
  padding-left: 36px;

  @media (max-width: 1050px) {
    padding-left: 0;
  }
`;

const KeyDetailsSeparator = styled.hr`
  margin: 0 0 0 -36px;
  padding: 0;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  width: calc(100% + 36px);

  @media (max-width: 1050px) {
    display: none;
  }
`;

const SkeletonLocationEpisodeDropdown = styled.div`
  height: 34px;
  background-color: white;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid var(--black-25);
  display: flex;
  align-items: center;
  padding-left: 8px;
`;
const SkeletonLocationEpisodeDropdownInner = styled(SkeletonBlurb)`
  flex: 0.66;
  border-radius: 13px;
  height: 13px;
`;
